import api from './api';
import {
  Notification,
  NotificationFilters,
  NotificationResponse,
  SingleNotificationResponse,
  NotificationCountResponse,
  BulkOperationResponse,
} from '@/types/notification.types';

/**
 * Notification Service
 * Handles all notification-related API calls
 */

const BASE_URL = '/notifications';

/**
 * Get all notifications for the authenticated user
 */
export const getNotifications = async (filters?: NotificationFilters): Promise<Notification[]> => {
  const params = new URLSearchParams();

  if (filters?.read !== undefined) {
    params.append('read', filters.read.toString());
  }
  if (filters?.type) {
    params.append('type', filters.type);
  }
  if (filters?.limit) {
    params.append('limit', filters.limit.toString());
  }
  if (filters?.offset) {
    params.append('offset', filters.offset.toString());
  }

  const queryString = params.toString();
  const url = queryString ? `${BASE_URL}?${queryString}` : BASE_URL;

  const response = await api.get<NotificationResponse>(url);
  return response.data.data;
};

/**
 * Get unread notification count
 */
export const getUnreadCount = async (): Promise<number> => {
  const response = await api.get<NotificationCountResponse>(`${BASE_URL}/unread-count`);
  return response.data.data.count;
};

/**
 * Mark a notification as read
 */
export const markAsRead = async (notificationId: string): Promise<Notification> => {
  const response = await api.patch<SingleNotificationResponse>(`${BASE_URL}/${notificationId}/read`);
  return response.data.data;
};

/**
 * Mark all notifications as read
 */
export const markAllAsRead = async (): Promise<number> => {
  const response = await api.patch<BulkOperationResponse>(`${BASE_URL}/mark-all-read`);
  return response.data.data.count;
};

/**
 * Delete a notification
 */
export const deleteNotification = async (notificationId: string): Promise<void> => {
  await api.delete(`${BASE_URL}/${notificationId}`);
};

/**
 * Delete all read notifications
 */
export const deleteAllRead = async (): Promise<number> => {
  const response = await api.delete<BulkOperationResponse>(`${BASE_URL}/read`);
  return response.data.data.count;
};

/**
 * Poll for new notifications (for real-time updates)
 */
export const pollNotifications = async (lastCheckTimestamp?: string): Promise<Notification[]> => {
  const filters: NotificationFilters = {
    read: false,
    limit: 50,
  };

  return getNotifications(filters);
};
